import { trackEnquiryClick } from '@propertypal/shared/src/services/analytics';
import { ImportantPlaceResponse } from '@propertypal/shared/src/types/important-places';
import { Property } from '@propertypal/shared/src/types/property';
import { getGalleryImages } from '@propertypal/shared/src/utils/property';
import requiresEligibilityConfirmation from '@propertypal/shared/src/utils/property/eligibilityCriteria';
import { ALL_PHASES_VALUE } from '@propertypal/shared/src/utils/property/propertyPhase';
import BroadbandTable from '@propertypal/web-ui/src/broadband/BroadbandTable';
import CoOwnershipJumbo from '@propertypal/web-ui/src/jumbotrons/CoOwnershipJumbo';
import { MAX_WIDTH } from '@propertypal/web-ui/src/layout/PageWrapper.style';
import { LeafletMarker } from '@propertypal/web-ui/src/map/LeafletMap';
import ImageModal from '@propertypal/web-ui/src/modal/ImageModal';
import WhiteModal from '@propertypal/web-ui/src/modal/WhiteModal';
import AgentAccreditations from '@propertypal/web-ui/src/property/AgentAccreditations';
import ContactTable from '@propertypal/web-ui/src/property/ContactTable';
import CurrencyDirectAd from '@propertypal/web-ui/src/property/CurrencyDirectAd';
import DevelopmentHeader from '@propertypal/web-ui/src/property/DevelopmentHeader';
import KeyInfoList from '@propertypal/web-ui/src/property/KeyInfoList';
import OtherDevelopments from '@propertypal/web-ui/src/property/OtherDevelopments';
import PropertyAgentBar from '@propertypal/web-ui/src/property/PropertyAgentBar';
import PropertyAgentBox, { PropertyAgentBoxProps } from '@propertypal/web-ui/src/property/PropertyAgentBox.pn';
import PropertyAttributes from '@propertypal/web-ui/src/property/PropertyAttributes';
import PropertyDescription from '@propertypal/web-ui/src/property/PropertyDescription.pn';
import { DescriptionBox } from '@propertypal/web-ui/src/property/PropertyDescription.style.pn';
import PropertyImportantPlaces from '@propertypal/web-ui/src/property/PropertyImportantPlaces.pn';
import PropertyRooms from '@propertypal/web-ui/src/property/PropertyRooms';
import PropertySlider, { PropertySliderProps } from '@propertypal/web-ui/src/property/PropertySlider';
import PropertySummary from '@propertypal/web-ui/src/property/PropertySummary.pn';
import RelatedLinks from '@propertypal/web-ui/src/property/RelatedLinks';
import ShowHomesBar from '@propertypal/web-ui/src/property/ShowHomesBar';
import SimilarProperties from '@propertypal/web-ui/src/property/SimilarProperties';
import SimilarPropertiesLite from '@propertypal/web-ui/src/property/SimilarPropertiesLite.pn';
import SlideshowLinks, { SlideshowLinksProps } from '@propertypal/web-ui/src/property/SlideshowLinks';
import PropertyBanner from '@propertypal/web-ui/src/property-box/PropertyBanner';
import Breadcrumbs from '@propertypal/web-ui/src/search/Breadcrumbs';
import { SubHeading } from '@propertypal/web-ui/src/typography';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import React, { useMemo, useState } from 'react';
import AdBox from '../ads/AdBox';
import { PROPERTY_IMAGE_LOCATOR, PropertyDetailsContentProps, routerUpdate } from './PropertyDetails';
import {
  DesktopAgentBox,
  DetailsContent,
  DetailsContentAdditional,
  DetailsContentMain,
  DetailsWrapper,
  DevDescriptionContainer,
  MobileAgentBox,
  PNDetails,
  PropertyTypesWrapper,
  StickyAdWrapper,
} from './PropertyDetailsContent.style.pn';
import PropertyPdfBar from './PropertyPdfBar';
import PropertyTypesCarousel from './PropertyTypesCarousel';
import PropertyTypesModal from './PropertyTypesModal';
import PropertyUnits from './PropertyUnits';

const AD_BOX_SIDE_SIZES = [[300, 250], [300, 600], [160, 600], 'Fluid'];
const AD_BOX_MIDDLE_SIZES = [[320, 100], [300, 250], [336, 280], 'fluid'];
const AD_BOX_SPONSOR_SIZES = [[1, 1], 'fluid'];

const BrokerCalculator = dynamic(() => import('@propertypal/web-ui/src/property/BrokerCalculator'), { ssr: false });
const LegalFeesCalculatorLite = dynamic(() => import('@propertypal/web-ui/src/property/LegalFeesCalculatorLite.pn'), {
  ssr: false,
});
const BrochureModal = dynamic(() => import('./BrochureModal'), { ssr: false });
const EnquiryModal = dynamic(() => import('./EnquiryModal'), { ssr: false });
const EligibilityConfirmationModal = dynamic(() => import('./EligibilityConfirmationModal'), { ssr: false });
const GoogleMap = dynamic(() => import('../map/GoogleMap'), { ssr: false });
const PropertyMapModal = dynamic(() => import('./PropertyMapModal'), { ssr: false });
const PropertySlideshowModal = dynamic(() => import('./PropertySlideshowModal.pn'), { ssr: false });
const ShareModal = dynamic(() => import('./ShareModal'), { ssr: false });
const StampDutyModal = dynamic(() => import('./StampDutyModal'), { ssr: false });
const NotesModal = dynamic(() => import('./NotesModal'), { ssr: false });
const ImportantPlacesModal = dynamic(() => import('./ImportantPlacesModal.pn'), { ssr: false });
const LeafletMap = dynamic(() => import('@propertypal/web-ui/src/map/LeafletMap'), { ssr: false });

interface PropertyDescriptionBoxProps {
  property: Property;
  updateSectionUrl: (nextSection: string) => void;
}

const PropertyDescriptionBox: React.FC<PropertyDescriptionBoxProps> = ({ property, updateSectionUrl }) => {
  if (property.featuredDevelopmentContent?.hideAdditionalInfo) {
    return null;
  }

  return (
    <div data-testid="pn-description-box">
      <PropertyDescription property={property} onReadMore={() => updateSectionUrl('/description')} />
    </div>
  );
};

interface PropertyTypesProps
  extends Pick<
    PropertyDetailsContentProps,
    | 'property'
    | 'isFeaturedDevelopment'
    | 'similarProperties'
    | 'fetchSimilarProperties'
    | 'onFavourite'
    | 'updateSectionUrl'
  > {
  phase: string;
  setPhase: (phase: string) => void;
}

const PropertyTypes: React.FC<PropertyTypesProps> = ({
  property,
  isFeaturedDevelopment,
  similarProperties,
  fetchSimilarProperties,
  onFavourite,
  updateSectionUrl,
  phase,
  setPhase,
}) => {
  return (
    <PropertyTypesWrapper>
      {!!property.properties?.length && property.published && (
        <>
          <PropertyTypesCarousel
            onFavourite={onFavourite}
            properties={property.properties}
            phase={phase}
            setPhase={setPhase}
            onViewAll={() => updateSectionUrl('/property-types')}
          />
        </>
      )}

      {property.type === 'property' && property.development && isFeaturedDevelopment && (
        <SimilarProperties
          background="transparent"
          properties={similarProperties}
          onInView={fetchSimilarProperties}
          title="More from this development"
          hideOnEmpty
          onFavourite={onFavourite}
        />
      )}
    </PropertyTypesWrapper>
  );
};

interface PropertyLocationProps
  extends Pick<PropertyDetailsContentProps, 'property' | 'showMapModal' | 'updateSectionUrl' | 'loggedIn'> {
  importantPlaces?: ImportantPlaceResponse[];
  showImportantPlacesModal: boolean;
  titleSuffix?: string;
  marginTop?: number;
}

const PropertyLocation: React.FC<PropertyLocationProps> = ({
  property,
  showMapModal,
  updateSectionUrl,
  importantPlaces,
  showImportantPlacesModal,
  loggedIn,
  titleSuffix,
  marginTop = 60,
}) => {
  const leaftletMarkers = useMemo(() => {
    if (importantPlaces?.length) {
      const markers: LeafletMarker[] = importantPlaces.map((p) => ({
        lat: p.coordinate.latitude,
        lng: p.coordinate.longitude,
        name: p.name,
      }));

      return markers;
    }
    return undefined;
  }, [importantPlaces]);

  return (
    <>
      {!showMapModal && (
        <div style={{ marginTop, overflow: 'hidden' }} className="hide-print">
          <SubHeading fontSize={20} mb={10}>
            Location{titleSuffix && ` ${titleSuffix}`}
          </SubHeading>
          <LeafletMap
            lat={property.coordinate?.latitude}
            lng={property.coordinate?.longitude}
            zoom={property.coordinate?.mapZoomLevel}
            onExpand={() => updateSectionUrl('/map')}
            markers={leaftletMarkers}
          />
        </div>
      )}

      <PropertyImportantPlaces
        importantPlaces={importantPlaces}
        showImportantPlacesModal={showImportantPlacesModal}
        loggedIn={loggedIn}
        propertyPath={property.path}
      />
    </>
  );
};

const PropertyDetailsContentPN: React.FC<PropertyDetailsContentProps> = ({
  property,
  loggedIn,
  adSuffix,
  updateSectionUrl,
  imageIndex,
  updatePhotoUrl,
  handleImageClick,
  showSlideshowModal,
  getReplaceUrl,
  hasSitemap,
  hasFeaturedBrochure,
  urlParts,
  favourited,
  toggleFavourited,
  leadId,
  handleInfoClick,
  buyingStatus,
  callAgent,
  modal,
  showPhoneModal,
  ukResident,
  mortgageCalculator,
  onFavourite,
  handleShare,
  isFeaturedDevelopment,
  similarProperties,
  fetchSimilarProperties,
  showMapModal,
  showLegalFees,
  setEnquiryAccountNumber,
  otherDevelopments,
  fetchOtherDevelopments,
  modalImage,
  setModalImage,
  showStampDutyModal,
  setBuyingStatus,
  setUkResident,
  handleBack,
  closeModal,
  showEnquiryModal,
  showRegisterInterestModal,
  enquiryAccountNumber,
  setLeadId,
  showBrochureModal,
  showShareModal,
  isAdObscuringModalOpen,
  notes,
  setNotes,
  showPropertyTypesModal,
  showDescription,
}) => {
  const router = useRouter();
  const images = getGalleryImages(property, PROPERTY_IMAGE_LOCATOR[0], true, true, false, true);

  const [revealNumber, setRevealNumber] = useState<string | null>(null);
  const [importantPlaces, setImportantPlaces] = useState<ImportantPlaceResponse[] | undefined>(
    property.importantPlaces,
  );
  const [phase, setPhase] = useState<string>(ALL_PHASES_VALUE);
  const [showEligibilityConfirmModal, setShowEligibilityConfirmModal] = useState(false);
  const [pendingEligibilityAction, setPendingEligibilityAction] = useState<(() => void) | undefined>();

  const showNotesModal = !!(loggedIn && urlParts.includes('notes') && property.published);
  const showImportantPlacesModal =
    loggedIn && urlParts.includes('important-places') && (importantPlaces?.length || 0) < 5;

  const closeEligibilityModal = () => {
    setShowEligibilityConfirmModal(false);
    setPendingEligibilityAction(undefined);
  };

  const withEligibilityConfirmation = (action: () => void) => {
    if (!requiresEligibilityConfirmation(property)) {
      action();
      return;
    }

    setPendingEligibilityAction(() => action);
    setShowEligibilityConfirmModal(true);
  };

  const confirmEligibility = () => {
    const action = pendingEligibilityAction;

    closeEligibilityModal();
    if (action) action();
  };

  const agentBoxProps: PropertyAgentBoxProps = {
    property,
    onCallClick: (location) =>
      withEligibilityConfirmation(() =>
        callAgent(property, undefined, undefined, property.type === 'development' ? 'DDP' : 'PDP', location),
      ),
    onShowNumberClick: (location, accountNumber) => {
      withEligibilityConfirmation(() => {
        const contacts = property?.contacts?.all;
        const items = contacts?.filter((c) => c.phoneNumber?.displayNumber);
        if (items?.length === 1) setRevealNumber(items[0].phoneNumber!.displayNumber);
        showPhoneModal(
          property,
          undefined,
          undefined,
          property.type === 'development' ? 'DDP' : 'PDP',
          location,
          items?.length === 1,
          accountNumber,
        );
        updateSectionUrl('/call-the-agent');
      });
    },
    onEnquiryClick: (accountNumber) => {
      withEligibilityConfirmation(() => {
        setEnquiryAccountNumber(accountNumber || '');
        updateSectionUrl('/enquiry');
      });
    },
    onRegisterInterestClick: () => updateSectionUrl('/register-interest'),
    registerInterest: property.registerInterest,
    leadId,
    revealNumber,
  };

  const sliderProps = useMemo(() => {
    if (images.length > 0) {
      const sProps: Omit<PropertySliderProps, 'maxHeight'> = {
        alt: property.addressLine1,
        images,
        imageIndex,
        setImageIndex: updatePhotoUrl,
        onClick: handleImageClick,
        onCounterClick: handleImageClick,
        altCounter: true,
        renderImageOverlay: () => <PropertyBanner property={property} />,
        disableEvents: showSlideshowModal,
        property,
      };

      return sProps;
    }
    return null;
  }, [
    property.addressLine1,
    images,
    imageIndex,
    updatePhotoUrl,
    handleImageClick,
    handleImageClick,
    showSlideshowModal,
  ]);

  const slideshowLinksProps: SlideshowLinksProps = {
    getReplaceUrl,
    property,
    hasSitemap,
  };

  const locationProps: PropertyLocationProps = {
    importantPlaces,
    loggedIn,
    property,
    showImportantPlacesModal,
    showMapModal,
    updateSectionUrl,
  };

  const propertyTypesProps: PropertyTypesProps = {
    property,
    isFeaturedDevelopment,
    similarProperties,
    fetchSimilarProperties,
    onFavourite,
    updateSectionUrl,
    phase,
    setPhase,
  };

  return (
    <PNDetails data-testid="pp-details-page" className="pp-background-color">
      <Breadcrumbs crumbs={property.breadcrumbs} suffix="to Results" arrow path={property.path} />

      <PropertyAgentBar property={property} hideOnMobile />

      {property.showHomeOpeningTime && <ShowHomesBar property={property} />}

      {property.coordinate &&
        !urlParts.includes('map') &&
        images.length === 0 &&
        !property.featuredDevelopmentContent?.contentTop && (
          <GoogleMap
            width="100%"
            height={300}
            lat={property.coordinate.latitude}
            lng={property.coordinate.longitude}
            zoom={property.coordinate.mapZoomLevel}
            markers={[
              {
                id: 1,
                lat: property.coordinate.latitude,
                lng: property.coordinate.longitude,
              },
            ]}
          />
        )}

      {property.featuredDevelopmentContent?.contentTop && (
        <DevelopmentHeader
          html={property.featuredDevelopmentContent.contentTop}
          property={property}
          onOpenSlideshowClick={() => updateSectionUrl('/slideshow', 0)}
          onImageClick={(index) => updateSectionUrl('/slideshow', index)}
        />
      )}

      {!property.featuredDevelopmentContent?.contentTop && sliderProps && (
        <PropertySlider {...sliderProps} maxHeight="65vh" />
      )}

      <AdBox
        id="PDPsponsor_desktop"
        config="PDPsponsor_desktop"
        sizes={AD_BOX_SPONSOR_SIZES}
        hideUnder={970}
        renderedStyle={{ margin: '15px auto 30px', maxWidth: MAX_WIDTH - 100 }}
        collapse
      />
      <AdBox
        id="PDPsponsor_mobile"
        config="PDPsponsor_mobile"
        sizes={AD_BOX_SPONSOR_SIZES}
        hideOver={969}
        renderedStyle={{ margin: '15px auto 30px' }}
        collapse
      />

      <DetailsContent data-testid="pp-top-section">
        <DetailsContentMain>
          <SlideshowLinks {...slideshowLinksProps} />

          <DetailsWrapper>
            <div>
              <PropertySummary
                property={property}
                favourited={favourited}
                toggleFavourited={toggleFavourited}
                onShare={handleShare}
                loggedIn={loggedIn}
                hasFeaturedBrochure={hasFeaturedBrochure}
                showNotesModal={showNotesModal}
                notes={notes}
              />

              {property.keyInfo && (
                <KeyInfoList
                  property={property}
                  onClick={handleInfoClick}
                  buyingStatus={buyingStatus}
                  ukResident={ukResident}
                  mortgageCalculator={mortgageCalculator}
                  small
                  showLegalFees={showLegalFees}
                  disableCoOwnership={!property.coOwnershipOnly}
                />
              )}

              <PropertyAttributes property={property} />

              {property.units && (
                <PropertyUnits
                  property={property}
                  hasSitemap={hasSitemap}
                  onSiteMapClick={(unit) => updateSectionUrl(`/sitemap/${unit.name}`)}
                />
              )}

              <MobileAgentBox>
                <PropertyAgentBox {...agentBoxProps} />
              </MobileAgentBox>

              <CurrencyDirectAd property={property} />
            </div>

            <AdBox
              id="PDPmiddle_mobile"
              config="PDPmiddle_mobile"
              sizes={AD_BOX_MIDDLE_SIZES}
              hideOver={780}
              obscured={isAdObscuringModalOpen}
              containerStyle={{
                marginTop: 25,
                marginBottom: 45,
                paddingTop: 35,
                paddingBottom: 35,
                background: '#f5f5f5',
              }}
              displayMargin={300}
              declaration
              refresh
              timedRefresh
            />

            {(!isFeaturedDevelopment || property.type !== 'development') && (
              <PropertyDescriptionBox property={property} updateSectionUrl={updateSectionUrl} />
            )}

            <PropertyLocation {...locationProps} />

            {(!!property.account?.accreditations?.length || !!property.account.missingAccreditations?.length) &&
              property.type === 'property' &&
              !property.featuredDevelopmentContent && (
                <>
                  <SubHeading mt={50}>Agent Accreditations</SubHeading>
                  <AgentAccreditations
                    accreditations={property.account.accreditations}
                    missingAccreditations={property.account.missingAccreditations}
                  />
                </>
              )}

            <div id="broadband-table" />

            <div className="hide-print">
              <BroadbandTable property={property} />
            </div>

            {mortgageCalculator.hasMortgageCalculator && (
              <>
                <div id="calculator-marker" />

                <div className="hide-print">
                  <BrokerCalculator property={property} mortgageCalculator={mortgageCalculator} />
                </div>
              </>
            )}

            {showLegalFees && (
              <div className="hide-print">
                <div id="legal-fees-marker" />
                <LegalFeesCalculatorLite property={property} />
              </div>
            )}

            {/* {shouldDisplayGuides(property) && (
            <div
              className="hide-print"
            >
              <ContentHubGuidesPreview />
            </div>
          )} */}

            <PropertyPdfBar
              property={property}
              onItemClick={(path) => routerUpdate(router, `${property.path}${path}`)}
              header="Downloads"
            />

            <DesktopAgentBox>
              <RelatedLinks property={property} />
            </DesktopAgentBox>
          </DetailsWrapper>
        </DetailsContentMain>
        <DetailsContentAdditional>
          <DetailsWrapper>
            <DesktopAgentBox>
              <PropertyAgentBox {...agentBoxProps} />
            </DesktopAgentBox>

            {property.type === 'property' && !property.development && (
              <SimilarPropertiesLite properties={similarProperties} onInView={fetchSimilarProperties} />
            )}

            <MobileAgentBox>
              <RelatedLinks property={property} />
            </MobileAgentBox>

            <StickyAdWrapper>
              <AdBox
                id={`PDPright_desktop_${adSuffix}`}
                config="PDPright_desktop"
                sizes={AD_BOX_SIDE_SIZES}
                hideUnder={980}
                obscured={isAdObscuringModalOpen}
                containerStyle={{
                  marginTop: 25,
                  marginBottom: 45,
                  paddingTop: 35,
                  paddingBottom: 35,
                  background: '#f5f5f5',
                }}
                displayMargin={250}
                declaration
                refresh
                timedRefresh
              />
            </StickyAdWrapper>
          </DetailsWrapper>
        </DetailsContentAdditional>
      </DetailsContent>

      {showNotesModal && (
        <NotesModal
          show={showNotesModal}
          onClose={closeModal}
          property={property}
          onNoteUpdate={setNotes}
          notes={notes}
        />
      )}

      {showImportantPlacesModal && (
        <ImportantPlacesModal
          show={showImportantPlacesModal}
          onClose={closeModal}
          importantPlaces={importantPlaces}
          coordinate={property.coordinate}
          onSubmit={(ip) => {
            setImportantPlaces(ip);
            handleBack();
          }}
          propertyId={property.id}
        />
      )}

      <ImageModal
        ariaLabel="EPC image popup"
        show={!!modalImage}
        onClose={() => setModalImage('')}
        imageUrl={modalImage}
      />

      {showStampDutyModal && (
        <StampDutyModal
          buyingStatus={buyingStatus}
          setBuyingStatus={setBuyingStatus}
          ukResident={ukResident}
          setUkResident={setUkResident}
          property={property}
          show={showStampDutyModal}
          onClose={closeModal}
        />
      )}

      {showMapModal && (
        <PropertyMapModal
          show={showMapModal}
          property={property}
          onClose={closeModal}
          adSuffix={adSuffix}
          hideSchoolData
          importantPlaces={importantPlaces}
        />
      )}

      {showSlideshowModal && (
        <PropertySlideshowModal
          imageIndex={imageIndex}
          setImageIndex={updatePhotoUrl}
          show={showSlideshowModal}
          property={property}
          onClose={handleBack}
          onTabClick={(tab, index) => updateSectionUrl(`/${tab}`, index)}
          url={router.asPath}
          imageLocator={PROPERTY_IMAGE_LOCATOR[0]}
          hasSitemap={hasSitemap}
          adSuffix={adSuffix}
        />
      )}

      {(showEnquiryModal || showRegisterInterestModal) && (
        <EnquiryModal
          accountNumber={enquiryAccountNumber}
          show={showEnquiryModal || showRegisterInterestModal}
          onClose={closeModal}
          property={property}
          registerInterestModal={showRegisterInterestModal}
          onSuccess={setLeadId}
          loggedIn={loggedIn}
          loggedInUser={property.loggedInUser}
          page={property.type === 'development' ? 'DDP' : 'PDP'}
        />
      )}

      {showBrochureModal && <BrochureModal show={showBrochureModal} onClose={closeModal} property={property} />}

      {showShareModal && (
        <ShareModal show={router.asPath.includes('/share')} onClose={closeModal} property={property} />
      )}

      {showEligibilityConfirmModal && (
        <EligibilityConfirmationModal
          show={showEligibilityConfirmModal}
          onClose={closeEligibilityModal}
          onConfirm={confirmEligibility}
        />
      )}

      {modal}

      {showPropertyTypesModal && property.properties && (
        <PropertyTypesModal
          show={showPropertyTypesModal}
          onClose={closeModal}
          properties={property.properties}
          phase={phase}
          setPhase={setPhase}
          onFavourite={onFavourite}
        />
      )}

      <PropertyTypes {...propertyTypesProps} />

      {showDescription && property.description && (
        <WhiteModal show={showDescription} onClose={closeModal} title="Additional Information">
          <DescriptionBox style={{ marginTop: -15 }} dangerouslySetInnerHTML={{ __html: property.description }} />
          {!!property?.rooms?.length && <PropertyRooms property={property} />}
        </WhiteModal>
      )}

      {isFeaturedDevelopment &&
        property.type === 'development' &&
        property.description &&
        !property.featuredDevelopmentContent?.hideAdditionalInfo && (
          <DevDescriptionContainer>
            <PropertyDescriptionBox property={property} updateSectionUrl={updateSectionUrl} />
          </DevDescriptionContainer>
        )}

      {property.featuredDevelopmentContent && (
        <div
          className="pp-development-content"
          dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.contentMiddle }}
        />
      )}

      {property.type === 'development' && (
        <OtherDevelopments data={otherDevelopments} onInView={fetchOtherDevelopments} small />
      )}

      {property.featuredDevelopmentContent && (
        <div
          className="pp-development-content"
          dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.contentBottom }}
        />
      )}

      {(property.type === 'development' || property.development) &&
        property.contacts &&
        property.contacts.all.length > 1 &&
        !property.featuredDevelopmentContent?.hideAgentContact &&
        property.published && (
          <div className="hide-print">
            <ContactTable
              property={property}
              onEnquiryClick={(contact) => {
                trackEnquiryClick('DDP', 'Contact Table', property, contact.accountNumber);
                setEnquiryAccountNumber(contact.accountNumber);
                updateSectionUrl('/enquiry');
              }}
              onRegisterInterestClick={(contact) => {
                setEnquiryAccountNumber(contact.accountNumber);
                updateSectionUrl('/register-interest');
              }}
              registerInterest={property.registerInterest}
            />
          </div>
        )}

      <CoOwnershipJumbo property={property} containerStyle={{ margin: '0px 0 -40px' }} />

      {property.featuredDevelopmentContent?.css && (
        <>
          <style>
            {`
              .pp-info-box { border-color: #dddddd; }
              .pp-info-box-header { background-color: #eeeeee; }
              .pp-btn { background-color: #353535; }
              .pp-btn:hover { background-color: #4a4a4a; }
              .pp-primary-text { color: #353535 !important; }
              .pp-school-table-title svg { color: #353535; } 
              .pp-school-table-row span { border-color: #d8d8d8; }
              .pp-multi-select-pill { background-color: #353535; }
              .pp-validation-error { background-color: #d0021b; } 
              .pp-input-error { box-shadow: inset 0 0 0 2px #d0021b !important; }
              .pp-development-content { background-color: #fff; }
              .pp-development-content h1,
              .pp-development-content h2,
              .pp-development-content h3,
              .pp-development-content h4,
              .pp-development-content h5,
              .pp-development-content h6,
              .pp-development-content p {
                margin: revert;
                line-height: revert;
              }
            `}
          </style>
          <style dangerouslySetInnerHTML={{ __html: property.featuredDevelopmentContent.css }} />
        </>
      )}
    </PNDetails>
  );
};

export default PropertyDetailsContentPN;
